import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { statusList, statusObject } from "../../../components/utils/allProductStatus";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { useProductsSearch } from "./useProductsSearch";

function formatStatusLabel(statuses) {
  if (!statuses?.length) {
    return "Todos";
  }
  if (statuses.length === 1) {
    return statusObject[statuses[0]] ?? statuses[0];
  }
  return `${statuses.length} status`;
}

export default function MobileProductsList({ user }) {
  const navigate = useNavigate();
  const { products, loading, error, filters, setFilters, search, refresh } =
    useProductsSearch(user);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    search();
  }, [search]);

  const openFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setFiltersOpen(false);
    search(draftFilters);
  };

  const handleStatusToggle = (status) => {
    setDraftFilters((current) => {
      const selected = current.status ?? [];
      if (selected.includes(status)) {
        return { ...current, status: selected.filter((item) => item !== status) };
      }
      return { ...current, status: [...selected, status] };
    });
  };

  const handleCreate = () => {
    navigate("/manafix/products/create");
  };

  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds([]);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const toggleSelected = (productId) => {
    setSelectedIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const goToTagGenerator = () => {
    if (!selectedIds.length) return;
    navigate("/manafix/serials/tags", {
      state: { origin: "products", products: selectedIds },
    });
  };

  const goToRegisterSerials = () => {
    if (!selectedIds.length) return;
    navigate("/manafix/serials/register", {
      state: { origin: "products", products: selectedIds },
    });
  };

  const activeFilterCount =
    (filters.id ? 1 : 0) +
    (filters.delivery ? 1 : 0) +
    (filters.store ? 1 : 0) +
    (filters.status?.length && filters.status.length !== 1 ? 1 : 0);

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06]"
        >
          Criar
        </button>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
        >
          Atualizar
        </button>
        {selectMode ? (
          <>
            <button
              type="button"
              onClick={exitSelectMode}
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={!selectedIds.length}
              onClick={goToRegisterSerials}
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-green-700 px-3 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Avaliar ({selectedIds.length})
            </button>
            <button
              type="button"
              disabled={!selectedIds.length}
              onClick={goToTagGenerator}
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Etiquetas ({selectedIds.length})
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={enterSelectMode}
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06]"
          >
            Selecionar
          </button>
        )}
      </div>

      <MobileSearchBar
        value={filters.q}
        onChange={(q) => setFilters((current) => ({ ...current, q }))}
        onSubmit={(q) => {
          const next = { ...filters, q: typeof q === "string" ? q : "" };
          setFilters(next);
          search(next);
        }}
        placeholder="Nome, CPF, e-mail ou rastreio"
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={openFilters}
          className="inline-flex min-h-[36px] items-center rounded-full border border-primary/50 bg-monao px-3 text-sm text-laranjinha/80 hover:bg-monao"
        >
          Filtros{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </button>
        <span className="inline-flex min-h-[36px] items-center rounded-full bg-primary/15 px-3 text-sm text-primary">
          Status: {formatStatusLabel(filters.status)}
        </span>
        {filters.delivery ? (
          <span className="inline-flex min-h-[36px] items-center rounded-full bg-monao px-3 text-sm text-laranjinha/80">
            Entrega: {filters.delivery}
          </span>
        ) : null}
      </div>

      {selectMode ? (
        <p className="border-b border-primary/30 bg-primary/10 px-4 py-2 text-sm text-laranjinha">
          Toque nos pedidos para selecionar. Depois use Etiquetas ou Avaliar.
        </p>
      ) : null}

      {loading ? (
        <div aria-label="Carregando pedidos" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar pedidos."}
          onRetry={refresh}
        />
      ) : products.length === 0 ? (
        <MobileEmpty
          title="Nenhum pedido encontrado"
          message={
            filters.q?.trim()
              ? `Nenhum resultado para “${filters.q.trim()}”. Tente outro termo.`
              : "Ajuste os filtros ou tente atualizar a lista."
          }
        />
      ) : (
        <MobileList>
          {products.map((product) => (
            <MobileListItem
              key={product._id}
              title={product.user?.name ?? "Sem nome"}
              subtitle={statusObject[product.status] ?? product.status}
              selectable={selectMode}
              selected={selectedIds.includes(product._id)}
              onClick={() => {
                if (selectMode) {
                  toggleSelected(product._id);
                  return;
                }
                navigate(`/manafix/products/${product._id}`);
              }}
            />
          ))}
        </MobileList>
      )}

      <MobileFilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filtros"
      >
        <div className="space-y-4">
          <label className="block text-sm font-medium text-laranjinha/80">
            ID do pedido
            <input
              type="text"
              value={draftFilters.id}
              onChange={(e) =>
                setDraftFilters((current) => ({ ...current, id: e.target.value }))
              }
              className="mt-1 w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
            />
          </label>

          <label className="block text-sm font-medium text-laranjinha/80">
            Entrega
            <select
              value={draftFilters.delivery}
              onChange={(e) =>
                setDraftFilters((current) => ({
                  ...current,
                  delivery: e.target.value,
                  store: e.target.value === "presencial" ? current.store : "",
                }))
              }
              className="mt-1 w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
            >
              <option value="">Todas</option>
              <option value="presencial">Presencial</option>
              <option value="correios">Correio</option>
            </select>
          </label>

          <fieldset>
            <legend className="text-sm font-medium text-laranjinha/80">Status</legend>
            <div className="mt-2 max-h-48 space-y-2 overflow-y-auto">
              {statusList.map((status) => (
                <label
                  key={status}
                  className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-monao"
                >
                  <input
                    type="checkbox"
                    checked={draftFilters.status?.includes(status) ?? false}
                    onChange={() => handleStatusToggle(status)}
                    className="h-5 w-5 rounded border-primary/50"
                  />
                  <span className="text-sm text-laranjinha">{statusObject[status]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary px-4 font-medium text-mgs-bg hover:bg-[#b47c06]"
          >
            Aplicar filtros
          </button>
        </div>
      </MobileFilterSheet>
    </div>
  );
}
